import type { FormEvent } from "react";
import { useState } from "react";
import { sendWrite } from "../../offline/writeQueue";
import { track } from "../../lib/events";
import { primaryButtonClass, Status } from "../ui";
import { FormField } from "./FormField";
import type {
  ContentItem,
  FormFieldDefinition,
  FormSubmission,
  FormValue,
  FormValues,
} from "../../types";

export function Form({ item, onComplete }: { item: ContentItem; onComplete?: () => void }) {
  const [submission, setSubmission] = useState<FormSubmission | null>(null);
  const [values, setValues] = useState<FormValues>({});
  const [done, setDone] = useState<"" | "sent" | "queued">("");
  const fields = (item.content.fields as FormFieldDefinition[] | undefined) || [];

  async function start() {
    const localRef = `local:${crypto.randomUUID()}`;
    const result = await sendWrite<FormSubmission>({
      method: "POST",
      path: `/api/teacher/forms/${item.id}/submissions`,
      createsRef: localRef,
    });
    setSubmission(result.queued ? { id: localRef } : result.data);
    track("form_opened", { item_id: item.id });
  }

  async function save(field: FormFieldDefinition, value: FormValue) {
    if (!submission) return;
    setValues((current) => ({ ...current, [field.id]: value }));
    await sendWrite({
      method: "PUT",
      path: `/api/teacher/submissions/${submission.id}/responses/${field.id}`,
      body: { value },
    });
    track("field_changed", { item_id: item.id, payload: { field: field.id } });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!submission) return;
    const result = await sendWrite({
      method: "POST",
      path: `/api/teacher/submissions/${submission.id}/submit`,
    });
    setDone(result.queued ? "queued" : "sent");
    onComplete?.();
    track("form_submitted", { item_id: item.id });
  }

  if (done) {
    return (
      <Status
        variant="success"
        title="Form submitted"
        message={
          done === "queued"
            ? "Your response is saved on this device and will sync when you're back online."
            : "Your response was saved."
        }
      />
    );
  }

  if (!submission) {
    return (
      <button className={primaryButtonClass} onClick={() => void start()}>
        Open form
      </button>
    );
  }

  return (
    <form className="grid gap-4" onSubmit={(event) => void submit(event)}>
      {fields.map((field) => (
        <FormField
          key={field.id}
          field={field}
          value={values[field.id]}
          onChange={(value) => void save(field, value)}
        />
      ))}
      <button className={primaryButtonClass}>Submit form</button>
    </form>
  );
}
